import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  Search, 
  Filter, 
  Download,
  Eye,
  Lock,
  UserX,
  UserCheck,
  FileText,
  AlertCircle
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [filterResource, setFilterResource] = useState("all");
  const [totalLogs, setTotalLogs] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(100);

  useEffect(() => {
    fetchAuditLogs();
  }, [page]);

  useEffect(() => {
    filterLogs();
  }, [logs, searchEmail, filterAction, filterResource]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/audit-logs`, {
        params: {
          skip: page * limit,
          limit: limit
        }
      });
      setLogs(response.data.logs);
      setTotalLogs(response.data.total);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Accès refusé. Seul le Super Admin peut consulter les logs.");
      } else {
        toast.error("Erreur lors du chargement des logs d'audit");
      }
      console.error("Error fetching audit logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = [...logs];

    // Filter by email
    if (searchEmail) {
      filtered = filtered.filter(log =>
        log.user_email.toLowerCase().includes(searchEmail.toLowerCase())
      );
    }

    // Filter by action
    if (filterAction !== "all") {
      filtered = filtered.filter(log => log.action === filterAction);
    }

    // Filter by resource
    if (filterResource !== "all") {
      filtered = filtered.filter(log => log.resource === filterResource);
    }

    setFilteredLogs(filtered);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "login":
        return <UserCheck className="w-4 h-4 text-green-500" />;
      case "login_failed":
      case "login_blocked":
        return <UserX className="w-4 h-4 text-red-500" />;
      case "logout":
        return <Lock className="w-4 h-4 text-gray-500" />;
      case "create":
        return <FileText className="w-4 h-4 text-blue-500" />;
      case "update":
        return <Eye className="w-4 h-4 text-orange-500" />;
      case "delete":
      case "delete_blocked":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      login: "Connexion",
      login_failed: "Échec connexion",
      login_blocked: "Connexion bloquée",
      logout: "Déconnexion",
      create: "Création",
      update: "Modification",
      delete: "Suppression",
      delete_blocked: "Suppression bloquée"
    };
    return labels[action] || action;
  };

  const getResourceLabel = (resource) => {
    const labels = {
      auth: "Authentification",
      client: "Client",
      contract: "Contrat",
      user: "Utilisateur",
      prospect: "Prospect",
      rdv: "Rendez-vous",
      devis: "Devis/Facture"
    };
    return labels[resource] || resource;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  const exportLogs = () => {
    const csvContent = [
      ["Date", "Email", "Action", "Ressource", "Détails", "IP"].join(","),
      ...filteredLogs.map(log => [
        formatDate(log.timestamp),
        log.user_email,
        getActionLabel(log.action),
        getResourceLabel(log.resource),
        JSON.stringify(log.details || {}),
        log.ip_address || "N/A"
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString()}.csv`;
    a.click();
    toast.success("Logs exportés avec succès");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Shield className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Audit & Logs</h1>
            <p className="text-gray-600">Traçabilité de toutes les actions</p>
          </div>
        </div>
        <Button onClick={exportLogs} className="flex items-center space-x-2">
          <Download className="w-4 h-4" />
          <span>Exporter CSV</span>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600">Total des logs</div>
            <div className="text-2xl font-bold text-gray-800">{totalLogs}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600">Connexions réussies</div>
            <div className="text-2xl font-bold text-green-600">
              {logs.filter(l => l.action === "login").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600">Échecs connexion</div>
            <div className="text-2xl font-bold text-red-600">
              {logs.filter(l => l.action === "login_failed").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600">Actions critiques</div>
            <div className="text-2xl font-bold text-orange-600">
              {logs.filter(l => l.action === "delete" || l.action === "delete_blocked").length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="w-5 h-5" />
            <span>Filtres</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search by email */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Rechercher par email
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="email@example.com"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Filter by action */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Filtrer par action
              </label>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger>
                  <SelectValue placeholder="Toutes les actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les actions</SelectItem>
                  <SelectItem value="login">Connexion</SelectItem>
                  <SelectItem value="login_failed">Échec connexion</SelectItem>
                  <SelectItem value="login_blocked">Connexion bloquée</SelectItem>
                  <SelectItem value="create">Création</SelectItem>
                  <SelectItem value="update">Modification</SelectItem>
                  <SelectItem value="delete">Suppression</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filter by resource */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Filtrer par ressource
              </label>
              <Select value={filterResource} onValueChange={setFilterResource}>
                <SelectTrigger>
                  <SelectValue placeholder="Toutes les ressources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les ressources</SelectItem>
                  <SelectItem value="auth">Authentification</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="contract">Contrat</SelectItem>
                  <SelectItem value="user">Utilisateur</SelectItem>
                  <SelectItem value="prospect">Prospect</SelectItem>
                  <SelectItem value="rdv">Rendez-vous</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {filteredLogs.length} log(s) affiché(s) sur {totalLogs}
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchEmail("");
                setFilterAction("all");
                setFilterResource("all");
              }}
            >
              Réinitialiser les filtres
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Historique des actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date & Heure
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Utilisateur
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Ressource
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Détails
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    IP
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      Aucun log trouvé
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {formatDate(log.timestamp)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {log.user_email}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center space-x-2">
                          {getActionIcon(log.action)}
                          <span>{getActionLabel(log.action)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {getResourceLabel(log.resource)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="max-w-xs truncate" title={JSON.stringify(log.details)}>
                          {log.details ? JSON.stringify(log.details) : "-"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.ip_address || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalLogs > limit && (
            <div className="mt-4 flex justify-center space-x-2">
              <Button
                variant="outline"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
              >
                Précédent
              </Button>
              <span className="px-4 py-2 text-sm text-gray-700">
                Page {page + 1} sur {Math.ceil(totalLogs / limit)}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage(page + 1)}
                disabled={(page + 1) * limit >= totalLogs}
              >
                Suivant
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
